import { useMemo, useState } from "react";
import { api } from "../api/apiClient";
import type { Shift, UserListEntry, VacationRequest } from "../api/types";
import { shiftPayField } from "../utils/shiftPayPayload";
import { parseFlexibleTime24 } from "../utils/time24";
import { parseOptionalTypedAmount } from "../utils/typedAmount";
import { AmountReadBack } from "./finances/AmountReadBack";
import { Modal } from "./Modal";
import { TimeInput24 } from "./TimeInput24";

interface DayShiftsModalProps {
  date: Date;
  locationId: string;
  shifts: Shift[];
  candidateUsers: UserListEntry[];
  /** G-8: approved vacations covering this day (read-only markers). */
  vacations?: VacationRequest[];
  canEdit: boolean;
  /** D-90: the server's `payVisible` — owners/admins read and set a shift's pay rate; nobody else sees the box. */
  payVisible: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toLocalISO(date: Date, timeHHMM: string): string {
  const [h, m] = timeHHMM.split(":").map(Number);
  const dt = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m, 0, 0);
  return dt.toISOString();
}

function timeFromIso(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function DayShiftsModal({
  date,
  locationId,
  shifts,
  candidateUsers,
  vacations = [],
  canEdit,
  payVisible,
  onClose,
  onChanged,
}: DayShiftsModalProps) {
  const dayShifts = useMemo(
    () => shifts.filter((s) => dateKey(new Date(s.plannedStart)) === dateKey(date)),
    [shifts, date],
  );

  const [error, setError] = useState<string | null>(null);

  const onCreated = async () => {
    setError(null);
    await onChanged();
  };

  return (
    <Modal
      title={date.toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      })}
      onClose={onClose}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        {error ? <div className="alert error">{error}</div> : null}

        {vacations.length > 0 ? (
          <div className="vacation-banner">
            <strong>On vacation this day:</strong>{" "}
            {vacations
              .map((v) => v.user.fullName)
              .join(", ")}
          </div>
        ) : null}

        {dayShifts.length === 0 ? (
          <p className="muted">No shifts scheduled for this day yet.</p>
        ) : (
          dayShifts.map((s) => (
            <ShiftEditor
              key={s.id}
              shift={s}
              canEdit={canEdit}
              payVisible={payVisible}
              onChanged={async () => {
                await onChanged();
              }}
              onError={setError}
            />
          ))
        )}

        {canEdit ? (
          <AddShiftForm
            date={date}
            locationId={locationId}
            candidateUsers={candidateUsers}
            vacations={vacations}
            payVisible={payVisible}
            onCreated={onCreated}
            onError={setError}
          />
        ) : null}
      </div>
    </Modal>
  );
}

function ShiftEditor({
  shift,
  canEdit,
  payVisible,
  onChanged,
  onError,
}: {
  shift: Shift;
  canEdit: boolean;
  payVisible: boolean;
  onChanged: () => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const date = new Date(shift.plannedStart);
  const [plannedStart, setPlannedStart] = useState(timeFromIso(shift.plannedStart));
  const [plannedEnd, setPlannedEnd] = useState(timeFromIso(shift.plannedEnd));
  const [actualStart, setActualStart] = useState(timeFromIso(shift.actualStart));
  const [actualEnd, setActualEnd] = useState(timeFromIso(shift.actualEnd));
  const [payRate, setPayRate] = useState(shift.payrollHourlyRateEur ?? "");
  const [notes, setNotes] = useState(shift.notes ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    onError(null);
    try {
      const ps = parseFlexibleTime24(plannedStart);
      const pe = parseFlexibleTime24(plannedEnd);
      if (!ps || !pe) {
        onError("Planned start and end must be valid 24-hour times (HH:mm).");
        setBusy(false);
        return;
      }
      let asIso: string | null = null;
      let aeIso: string | null = null;
      if (actualStart.trim()) {
        const t = parseFlexibleTime24(actualStart);
        if (!t) {
          onError("Actual start must be a valid 24-hour time (HH:mm) or empty.");
          setBusy(false);
          return;
        }
        asIso = toLocalISO(date, t);
      }
      if (actualEnd.trim()) {
        const t = parseFlexibleTime24(actualEnd);
        if (!t) {
          onError("Actual end must be a valid 24-hour time (HH:mm) or empty.");
          setBusy(false);
          return;
        }
        aeIso = toLocalISO(date, t);
      }
      const rateRead = parseOptionalTypedAmount(payRate);
      if (!rateRead.ok) {
        onError(`Pay rate: ${rateRead.message}`);
        setBusy(false);
        return;
      }
      const parsedRate = rateRead.value;
      await api.updateShift(shift.id, {
        plannedStart: toLocalISO(date, ps),
        plannedEnd: toLocalISO(date, pe),
        actualStart: asIso,
        actualEnd: aeIso,
        notes: notes.trim() ? notes.trim() : null,
        ...shiftPayField(payVisible, parsedRate),
      });
      await onChanged();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Delete shift for ${shift.user.fullName}?`)) return;
    setBusy(true);
    onError(null);
    try {
      await api.deleteShift(shift.id);
      await onChanged();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="day-shifts-row">
      <div className="day-shifts-row-head">
        <div>
          <strong>{shift.user.fullName}</strong>{" "}
          <span className="muted">· {shift.user.role.toLowerCase()}</span>
        </div>
        <span className={`badge ${shift.status === "COMPLETED" ? "success" : shift.status === "IN_PROGRESS" ? "primary" : "muted"}`}>
          {shift.status.replace("_", " ").toLowerCase()}
        </span>
      </div>

      {canEdit ? (
        <>
          <div className="modal-form">
            <div className="field">
              <label>Planned start</label>
              <TimeInput24 value={plannedStart} onChange={setPlannedStart} disabled={busy} />
            </div>
            <div className="field">
              <label>Planned end</label>
              <TimeInput24 value={plannedEnd} onChange={setPlannedEnd} disabled={busy} />
            </div>
            <div className="field">
              <label>Actual start</label>
              <TimeInput24
                value={actualStart}
                onChange={setActualStart}
                disabled={busy}
                allowEmpty
              />
            </div>
            <div className="field">
              <label>Actual end</label>
              <TimeInput24
                value={actualEnd}
                onChange={setActualEnd}
                disabled={busy}
                allowEmpty
              />
            </div>
            {payVisible ? (
              <div className="field">
                <label>Pay rate</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={payRate}
                  onChange={(e) => setPayRate(e.target.value)}
                  disabled={busy}
                />
                <AmountReadBack raw={payRate} />
              </div>
            ) : null}
          </div>
          <div className="field">
            <label>Notes</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="button-row">
            <button
              type="button"
              className="button danger"
              onClick={() => void remove()}
              disabled={busy}
            >
              Delete
            </button>
            <button
              type="button"
              className="button"
              onClick={() => void save()}
              disabled={busy}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      ) : (
        <div className="clock-card-times">
          <div>
            <strong>Planned</strong>
            {plannedStart}–{plannedEnd}
          </div>
          <div>
            <strong>Actual</strong>
            {actualStart || "—"}–{actualEnd || "—"}
          </div>
          {payVisible ? (
            <div>
              <strong>Pay rate</strong>
              {shift.payrollHourlyRateEur ?? "—"}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

function AddShiftForm({
  date,
  locationId,
  candidateUsers,
  vacations,
  payVisible,
  onCreated,
  onError,
}: {
  date: Date;
  locationId: string;
  candidateUsers: UserListEntry[];
  vacations: VacationRequest[];
  payVisible: boolean;
  onCreated: () => Promise<void>;
  onError: (msg: string | null) => void;
}) {
  const [userId, setUserId] = useState(candidateUsers[0]?.id ?? "");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("17:00");
  const [payRate, setPayRate] = useState("");
  const [busy, setBusy] = useState(false);

  // G-8 (owner decision 2026-07-29): vacation days BLOCK shift creation.
  // The backend enforces it (409); this mirrors it in the UI.
  const vacationForSelected = vacations.find((v) => v.userId === userId);

  const submit = async () => {
    setBusy(true);
    onError(null);
    try {
      const startNorm = parseFlexibleTime24(start);
      const endNorm = parseFlexibleTime24(end);
      if (!startNorm || !endNorm) {
        onError("Start and end must be valid 24-hour times (HH:mm).");
        setBusy(false);
        return;
      }
      const rateRead = parseOptionalTypedAmount(payRate);
      if (!rateRead.ok) {
        onError(`Pay rate: ${rateRead.message}`);
        setBusy(false);
        return;
      }
      const parsedRate = rateRead.value;
      await api.createShift({
        locationId,
        userId,
        plannedStart: toLocalISO(date, startNorm),
        plannedEnd: toLocalISO(date, endNorm),
        ...shiftPayField(payVisible, parsedRate),
      });
      await onCreated();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Could not create");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="day-shifts-row">
      <strong>Add a shift on this day</strong>
      <div className="modal-form">
        <div className="field full">
          <label htmlFor="add-user">Worker</label>
          <select
            id="add-user"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            {candidateUsers.length === 0 ? (
              <option value="">No eligible workers</option>
            ) : (
              candidateUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName} · {u.role.toLowerCase()}
                </option>
              ))
            )}
          </select>
          {vacationForSelected ? (
            <p className="vacation-inline-warning">
              {vacationForSelected.user.fullName} has approved vacation this
              day — shifts cannot be created on vacation days.
            </p>
          ) : null}
        </div>
        <div className="field">
          <label>Start</label>
          <TimeInput24 value={start} onChange={setStart} disabled={busy} />
        </div>
        <div className="field">
          <label>End</label>
          <TimeInput24 value={end} onChange={setEnd} disabled={busy} />
        </div>
        {payVisible ? (
          <div className="field">
            <label>Pay rate</label>
            <input
              type="text"
              inputMode="decimal"
              value={payRate}
              onChange={(e) => setPayRate(e.target.value)}
              disabled={busy}
            />
            <AmountReadBack raw={payRate} />
          </div>
        ) : null}
      </div>
      <div className="button-row">
        <button
          type="button"
          className="button"
          onClick={() => void submit()}
          disabled={!userId || busy || Boolean(vacationForSelected)}
        >
          {busy ? "Adding…" : "Add shift"}
        </button>
      </div>
    </div>
  );
}
